import { useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, MenuItem,
  Pagination, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useDebounce } from '../../../shared/hooks';
import { IListagemNfe, NfeService } from '../../../shared/services/api/Financeiro/fiscal';
import { INfeParaMdfe, lerNfeParaMdfe } from '../../../shared/services/api/Fiscal/MdfeService';

// Escolhe NF-e já emitidas no sistema pra incluir no MDF-e (ver
// DetalheDeMdfe.tsx). Lista pelo GET /nfe (mesma busca da listagem de NF-e) e,
// das marcadas, lê o XML autorizado (lerNfeParaMdfe) — só NF-e AUTORIZADA
// (status 2) tem XML, então as outras aparecem mas não dá pra marcar.
interface IDialogBuscarNfeProps {
  open: boolean;
  // Chaves já incluídas no MDF-e — aparecem marcadas como "já incluída".
  chavesJaIncluidas: string[];
  onClose: () => void;
  onConfirmar: (notas: INfeParaMdfe[], erros: string[]) => void;
}

const POR_PAGINA = 8;

export const DialogBuscarNfe: React.FC<IDialogBuscarNfeProps> = ({ open, chavesJaIncluidas, onClose, onConfirmar }) => {
  const { debounce } = useDebounce();
  const [tipo, setTipo] = useState<'n.dhemi' | 'n.nfe' | 'pa.nome'>('n.dhemi');
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [notas, setNotas] = useState<IListagemNfe[]>([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [erroLista, setErroLista] = useState('');
  const [marcadas, setMarcadas] = useState<Record<string, IListagemNfe>>({});
  const [lendo, setLendo] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCarregando(true);
    debounce(() => {
      NfeService.getAll(pagina, busca, tipo, POR_PAGINA).then((r) => {
        setCarregando(false);
        if (r instanceof Error) { setErroLista(r.message); setNotas([]); return; }
        setErroLista('');
        setNotas(r.data);
        setTotal(r.totalCount);
      });
    });
  }, [open, busca, tipo, pagina]);

  useEffect(() => {
    if (!open) { setMarcadas({}); setBusca(''); setPagina(1); }
  }, [open]);

  const alternar = (nota: IListagemNfe) => setMarcadas((atual) => {
    const novo = { ...atual };
    if (novo[nota.idnfe]) delete novo[nota.idnfe]; else novo[nota.idnfe] = nota;
    return novo;
  });

  const confirmar = async () => {
    setLendo(true);
    const selecionadas = Object.values(marcadas);
    const lidas = await Promise.all(selecionadas.map(async (nota) => ({ nota, resultado: await lerNfeParaMdfe(nota.idnfe) })));
    setLendo(false);
    const ok = lidas.filter((l) => !(l.resultado instanceof Error)).map((l) => l.resultado as INfeParaMdfe);
    const erros = lidas.filter((l) => l.resultado instanceof Error).map((l) => `NF-e ${l.nota.nfe}: ${(l.resultado as Error).message}`);
    onConfirmar(ok, erros);
  };

  const qtdMarcadas = Object.keys(marcadas).length;

  return (
    <Dialog open={open} onClose={lendo ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Buscar NF-e do sistema</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
          <TextField select size="small" label="Buscar por" value={tipo} sx={{ minWidth: 160 }}
            onChange={(e) => { setTipo(e.target.value as typeof tipo); setBusca(''); setPagina(1); }}>
            <MenuItem value="n.dhemi">Data de emissão</MenuItem>
            <MenuItem value="n.nfe">Número</MenuItem>
            <MenuItem value="pa.nome">Cliente</MenuItem>
          </TextField>
          {tipo === 'n.dhemi' ? (
            <TextField size="small" type="date" label="Data" InputLabelProps={{ shrink: true }} value={busca}
              onChange={(e) => { setBusca(e.target.value); setPagina(1); }} />
          ) : (
            <TextField size="small" label={tipo === 'n.nfe' ? 'Número da NF-e' : 'Nome do cliente'} value={busca} sx={{ flex: 1, minWidth: 200 }}
              onChange={(e) => { setBusca(e.target.value); setPagina(1); }} />
          )}
        </Box>

        {carregando && <LinearProgress sx={{ mb: 1 }} />}
        {erroLista && <Typography color="error.dark" variant="body2" mb={1}>{erroLista}</Typography>}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Emissão</TableCell>
              <TableCell align="right">Nº</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell align="right">Valor</TableCell>
              <TableCell align="center">Situação</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!carregando && !notas.length && (
              <TableRow><TableCell colSpan={6} align="center"><Typography variant="body2" color="text.secondary" py={2}>Nenhuma NF-e encontrada</Typography></TableCell></TableRow>
            )}
            {notas.map((nota) => {
              const autorizada = Number(nota.status) === 2;
              return (
                <TableRow key={nota.idnfe} hover={autorizada} onClick={() => autorizada && alternar(nota)} sx={{ cursor: autorizada ? 'pointer' : 'default', opacity: autorizada ? 1 : 0.55 }}>
                  <TableCell padding="checkbox"><Checkbox size="small" disabled={!autorizada} checked={!!marcadas[nota.idnfe]} /></TableCell>
                  <TableCell>{dayjs(nota.dhemi).format('DD/MM/YYYY')}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{nota.nfe}</TableCell>
                  <TableCell sx={{ maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nota.parceiros}</TableCell>
                  <TableCell align="right">{Number(nota.vnfe || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={autorizada ? 'Autorizada' : 'Não autorizada'} color={autorizada ? 'success' : 'default'} sx={{ height: 22 }} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {total > POR_PAGINA && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination size="small" page={pagina} count={Math.ceil(total / POR_PAGINA)} onChange={(_, p) => setPagina(p)} />
          </Box>
        )}
        {chavesJaIncluidas.length > 0 && (
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            Notas que já estão no MDF-e não são incluídas de novo.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={lendo}>Cancelar</Button>
        <Button variant="contained" disabled={!qtdMarcadas || lendo} onClick={confirmar}
          startIcon={lendo ? <CircularProgress size={16} color="inherit" /> : undefined}>
          {lendo ? 'Lendo as notas...' : `Incluir ${qtdMarcadas || ''} nota${qtdMarcadas === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
